import { Link } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { useThemeStore } from '@/stores/themeStore'
import { useLoginFlow } from '@/hooks/useLoginFlow'
import { resolvePublicAssetUrl } from '@/utils/resolvePublicAssetUrl'
import { LoginCredentialsForm } from '@/components/Login/LoginCredentialsForm'
import { LoginTwoFactorForm } from '@/components/Login/LoginTwoFactorForm'
import { LoginForgotPasswordForm } from '@/components/Login/LoginForgotPasswordForm'
import { LoginResetPasswordForm } from '@/components/Login/LoginResetPasswordForm'
import { LOGIN_BRAND } from '@/components/Login/loginBrand'
import { CONECTA_PLATFORM } from '@/config/conectaPlatform'
import { getInstanceDisplayName, getLoginFallbackLogo, isRocketInstance } from '@/config/instanceProfile'

export default function Login() {
  const { theme } = useThemeStore()
  const flow = useLoginFlow()

  // Login claro: logo de Identidade (tema claro) ou fallback do perfil.
  const themeLogoPath = theme.logoUrlLight || theme.logoUrl
  const logoSrc = themeLogoPath
    ? resolvePublicAssetUrl(themeLogoPath)
    : getLoginFallbackLogo()
  const brandColor = isRocketInstance ? '#d4145a' : LOGIN_BRAND.green
  // Login sempre em layout claro — independente do tema global do CRM.
  const pageBg = isRocketInstance ? '#f7f7f8' : LOGIN_BRAND.greenSoft
  const cardBg = '#ffffff'
  const cardBorder = isRocketInstance ? 'rgba(212, 20, 90, 0.18)' : LOGIN_BRAND.greenBorderLight
  const glowColor = isRocketInstance ? '#d4145a' : LOGIN_BRAND.green
  const stepProps = { flow, theme, brandColor }

  return (
    <div
      className="login-page"
      style={{
        background: pageBg,
        ['--login-green' as string]: brandColor,
        ['--login-green-muted' as string]: isRocketInstance
          ? 'rgba(212, 20, 90, 0.15)'
          : LOGIN_BRAND.greenMuted,
      }}
    >
      <div className="login-bg-effects" aria-hidden>
        <div
          className="login-glow glow-1"
          style={{ background: glowColor, filter: 'blur(120px)', opacity: 0.22 }}
        />
        <div
          className="login-glow glow-2"
          style={{ background: glowColor, filter: 'blur(120px)', opacity: 0.14 }}
        />
        <div
          className="login-grid-lines"
          style={{
            backgroundImage: `linear-gradient(${glowColor}14 1px, transparent 1px), linear-gradient(90deg, ${glowColor}14 1px, transparent 1px)`,
          }}
        />
      </div>

      <div className="login-container">
        <div
          className="login-card"
          style={{
            background: cardBg,
            border: `1px solid ${cardBorder}`,
            boxShadow: isRocketInstance
              ? `0 12px 40px rgba(212, 20, 90, 0.12), 0 0 0 1px ${cardBorder}`
              : `0 12px 40px ${LOGIN_BRAND.greenShadowLight}, 0 0 0 1px ${LOGIN_BRAND.greenBorderLight}`,
          }}
        >
          <div className="login-header">
            <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.5 }}>
              <img
                src={logoSrc}
                alt={theme.companyName || getInstanceDisplayName() || CONECTA_PLATFORM.name}
                className="login-logo"
                style={{
                  display: 'block',
                  margin: '0 auto 20px auto',
                  width: isRocketInstance ? 260 : 220,
                  filter: 'none',
                }}
                onError={(e) => {
                  e.currentTarget.style.display = 'none'
                }}
              />
            </motion.div>
          </div>

          {/* Honeypot anti-bot — fora do fluxo visual e de tab */}
          <input
            type="text"
            name="website"
            value={flow.honeypot}
            onChange={(e) => flow.setHoneypot(e.target.value)}
            style={{
              position: 'absolute',
              left: '-9999px',
              top: '-9999px',
              width: '1px',
              height: '1px',
              opacity: 0,
              pointerEvents: 'none',
            }}
            tabIndex={-1}
            autoComplete="off"
            aria-hidden
          />

          <AnimatePresence mode="wait">
            {flow.oidcRedirecting && !flow.showLocalCredentials ? (
              <motion.div
                key="oidc-redirect"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 16,
                  padding: '24px 0',
                  color: theme.textMuted || '#8a8a95',
                  fontSize: '0.9rem',
                  textAlign: 'center',
                }}
              >
                <span className="login-spinner" />
                <p style={{ margin: 0 }}>Entrando com Conecta ID…</p>
              </motion.div>
            ) : null}

            {flow.showLocalCredentials && flow.step === 'credentials' && (
              <LoginCredentialsForm {...stepProps} />
            )}
            {flow.step === 'twoFactor' && <LoginTwoFactorForm {...stepProps} />}
            {flow.step === 'forgotPassword' && <LoginForgotPasswordForm {...stepProps} />}
            {flow.step === 'resetPassword' && <LoginResetPasswordForm {...stepProps} />}
          </AnimatePresence>

          {isRocketInstance && (
            <p style={{ margin: '18px 0 0', textAlign: 'center', fontSize: 13, color: '#6b6b75' }}>
              É parceiro?{' '}
              <Link to='/parceiros' style={{ color: brandColor, fontWeight: 600 }}>
                Cadastre um lead
              </Link>
            </p>
          )}
        </div>
      </div>
    </div>
  )
}
